/**
 * AudioEndpoint - endpoint for audio signal routing
 *
 * Extends PropagatingEndpoint to get connection management and payload propagation.
 * Audio signals flow via Web Audio connect() (GainNode → AnalyserNode → target GainNode).
 * Metadata (e.g., spatial position) rides alongside on propagate() → receive().
 */

import { PropagatingEndpoint } from '@/runtime/PropagatingEndpoint';
import type { IEndpoint } from '@/runtime/IEndpoint';
import type { IRuntimeStore } from '@runtime/RuntimeStore';
import { type DataTypeSpec, type DataFlow } from '@/nodes/Endpoint';
import type { Payload } from '@/graph/Payload';
import type { IAudioContext, IGainNode, IAnalyserNode } from 'standardized-audio-context';
import { registerAudioEndpoint, unregisterAudioEndpoint, makeAudioKey } from './audioRegistry';
import { EndpointWithContext } from '../Plugin';

// Re-export registry functions for backwards compatibility
export { getAllAudioEndpoints, getFrequencyBins, getGainNode, getAudioEndpoint } from './audioRegistry';

export class AudioEndpoint extends PropagatingEndpoint {
  // Identity — set by PropagatingEndpoint's Object.assign, declared here to satisfy abstract
  readonly id: string;
  readonly dataType: DataTypeSpec;
  readonly dataFlow: DataFlow;

  // Audio-specific
  private audioContext: IAudioContext;
  private _gainNode: IGainNode<IAudioContext>;
  private analyser: IAnalyserNode<IAudioContext>;

  // Audio mute nodes (separate from PropagatingEndpoint's disabledConnections)
  private muteNodes: Map<IEndpoint, IGainNode<IAudioContext>> = new Map();

  constructor(config: EndpointWithContext, audioContext: IAudioContext, store: IRuntimeStore) {
    super(config, store);
    // Object.assign in super sets most fields from config.endpoint.
    // Ensure id is set (config.endpoint.id may be undefined before assignment).
    this.id = config.endpoint.id!;
    this.dataType = (config.endpoint.dataType ?? 'audio') as DataTypeSpec;
    this.dataFlow = (config.endpoint.dataFlow ?? 'stream') as DataFlow;

    this.audioContext = audioContext;
    this._gainNode = audioContext.createGain();
    this.analyser = audioContext.createAnalyser();
    this.analyser.fftSize = 256;
    this.gainNode.connect(this.analyser);

    registerAudioEndpoint(this.instancePath, this.node.id, this.id, this);
  }

  /** Node's id within the instance */
  get nodeId(): string {
    return this.node.id;
  }

  /** GainNode for this endpoint — plugins wire audio to/from this */
  get gainNode(): IGainNode<IAudioContext> {
    return this._gainNode;
  }

  /** AnalyserNode for level metering */
  getAnalyser(): IAnalyserNode<IAudioContext> {
    return this.analyser;
  }

  /** Composite key for this endpoint in the audio registry */
  get audioKey(): string {
    return makeAudioKey(this.instancePath, this.nodeId, this.id);
  }

  // ─────────────────────────────────────────────────────────────
  // Value / Metadata
  // ─────────────────────────────────────────────────────────────

  getValue(): Payload {
    return { value: this.getLevel() };
  }

  /**
   * Route metadata to onInternalChanged.
   * PropagatingEndpoint's notifyNode has no 'stream' case, so we override.
   */
  protected notifyNode(payload: any, source?: IEndpoint): void {
    if (source) {
      this.node.onInternalChanged(payload, this.id, source);
    }
  }

  /** Set a payload (metadata) and propagate to all targets */
  setValue(payload: Payload, source?: IEndpoint): void {
    if (source !== this) {
      this.notifyNode(payload, source);
    }
    this.propagate(payload, source);
  }

  /** Receive a payload (metadata) from a connected source */
  receive(payload: Payload, source: IEndpoint): void {
    this.setValue(payload, source);
  }

  // ─────────────────────────────────────────────────────────────
  // Audio Connection Wiring
  // ─────────────────────────────────────────────────────────────

  connectTo(target: IEndpoint): void {
    if (this.targetEndpoints.includes(target)) return;

    // PropagatingEndpoint handles target tracking and metadata propagation.
    super.connectTo(target);

    // Wire the Web Audio graph only for audio-to-audio connections
    if (target instanceof AudioEndpoint) {
      this.analyser.connect(target.gainNode);
    }
  }

  disconnectFrom(target: IEndpoint): void {
    // Audio graph cleanup only for audio-to-audio connections
    if (target instanceof AudioEndpoint) {
      const muteNode = this.muteNodes.get(target);
      if (muteNode) {
        this.analyser.disconnect(muteNode);
        muteNode.disconnect();
        this.muteNodes.delete(target);
      } else {
        try {
          this.analyser.disconnect(target.gainNode);
        } catch (e) {
          // May already be disconnected
        }
      }
    }

    // PropagatingEndpoint handles target tracking + onSourceDisconnected
    super.disconnectFrom(target);
  }

  setConnectionDisabled(target: IEndpoint, disabled: boolean): void {
    // Propagation-level disable (skips target in propagate())
    super.setConnectionDisabled(target, disabled);

    // Audio-level muting via GainNode(gain=0)
    this.setAudioMuted(target, disabled);
  }

  /**
   * Mute/unmute just the audio path to a target (insert/remove gain=0 node).
   * Does NOT affect propagation — metadata payloads still flow through.
   * Use this when you want to reroute audio but keep receiving metadata.
   */
  setAudioMuted(target: IEndpoint, muted: boolean): void {
    if (!(target instanceof AudioEndpoint)) return;
    if (!this.targetEndpoints.includes(target)) return;

    const existingMute = this.muteNodes.get(target);

    if (muted && !existingMute) {
      const muteNode = this.audioContext.createGain();
      muteNode.gain.value = 0;

      try {
        this.analyser.disconnect(target.gainNode);
      } catch (e) {
        // May already be disconnected
      }

      this.analyser.connect(muteNode);
      muteNode.connect(target.gainNode);
      this.muteNodes.set(target, muteNode);

    } else if (!muted && existingMute) {
      try {
        this.analyser.disconnect(existingMute);
        existingMute.disconnect();
      } catch (e) {
        // May already be disconnected
      }

      this.analyser.connect(target.gainNode);
      this.muteNodes.delete(target);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // Level / Frequency Analysis
  // ─────────────────────────────────────────────────────────────

  getTimeDomainData(): Float32Array {
    const data = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(data);
    return data;
  }

  getLevel(): number {
    const dataArray = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(dataArray);
    let sum = 0;
    for (const sample of dataArray) {
      sum += sample * sample;
    }
    return Math.min(1.0, Math.sqrt(sum / dataArray.length) * 2);
  }

  getFrequencyBins(binCount: number = 16): number[] {
    const freqData = new Uint8Array(this.analyser.frequencyBinCount);
    this.analyser.getByteFrequencyData(freqData);

    const binsPerGroup = Math.floor(this.analyser.frequencyBinCount / binCount);
    const bins: number[] = [];

    for (let i = 0; i < binCount; i++) {
      let sum = 0;
      const start = i * binsPerGroup;
      for (let j = 0; j < binsPerGroup; j++) {
        sum += freqData[start + j];
      }
      bins.push((sum / binsPerGroup) / 255);
    }

    return bins;
  }

  // ─────────────────────────────────────────────────────────────
  // Lifecycle
  // ─────────────────────────────────────────────────────────────

  dispose(): void {
    unregisterAudioEndpoint(this.instancePath, this.nodeId, this.id);

    for (const muteNode of this.muteNodes.values()) {
      muteNode.disconnect();
    }
    this.muteNodes.clear();

    for (const target of this.targetEndpoints) {
      if (target instanceof AudioEndpoint) {
        try {
          this.analyser.disconnect(target.gainNode);
        } catch (e) {
          // May already be disconnected via mute node
        }
      }
    }

    this.gainNode.disconnect();
    this.analyser.disconnect();

    super.dispose();
  }
}
